// src/components/Household/HouseholdInviteAccept.js
// F9 — Shared Household Budgets
// Prism Agent — UI/UX
//
// Page rendered at /invite/:token
// 1. Fetches invite preview (household name, owner)
// 2. Shows info to the logged-in user
// 3. "Accept" button → POST /households/invite/:token/accept
// 4. Redirects to /dashboard on success

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import api from '../../services/api';

const ROLE_LABELS = {
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

const ROLE_COLORS = {
  viewer: 'default',
  editor: 'primary',
  admin: 'warning',
};

export default function HouseholdInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | preview | accepted | error
  const [inviteData, setInviteData] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [accepting, setAccepting] = useState(false);

  // ── Load invite preview ───────────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No invite token provided.');
      return;
    }

    api
      .get(`/households/invite/${token}`)
      .then((res) => {
        const data = res.data;
        if (data.inviteStatus === 'accepted') {
          setStatus('error');
          setErrorMsg('This invite has already been accepted.');
          return;
        }
        if (data.inviteStatus === 'declined') {
          setStatus('error');
          setErrorMsg('This invite was declined.');
          return;
        }
        setInviteData(data);
        setStatus('preview');
      })
      .catch((err) => {
        setStatus('error');
        setErrorMsg(
          err.response?.data?.message ||
            'This invite link is invalid or has expired.',
        );
      });
  }, [token]);

  // ── Accept invite ─────────────────────────────────────────────────────────
  const handleAccept = async () => {
    setAccepting(true);
    try {
      await api.post(`/households/invite/${token}/accept`);
      setStatus('accepted');
      // Redirect after short delay so user sees success
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.message || 'Failed to accept invite. Please try again.',
      );
      setStatus('error');
    } finally {
      setAccepting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={2}
    >
      <Card sx={{ maxWidth: 460, width: '100%', boxShadow: 6, borderRadius: 3 }}>
        {/* ── Loading ──────────────────────────────────────────────────── */}
        {status === 'loading' && (
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Loading invite…
            </Typography>
          </CardContent>
        )}

        {/* ── Error ────────────────────────────────────────────────────── */}
        {status === 'error' && (
          <CardContent sx={{ py: 4, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Invite Unavailable
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {errorMsg}
            </Alert>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </CardContent>
        )}

        {/* ── Preview ──────────────────────────────────────────────────── */}
        {status === 'preview' && inviteData && (
          <CardContent sx={{ py: 4 }}>
            {/* Header */}
            <Stack alignItems="center" spacing={1} mb={3}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: 'primary.main',
                  mb: 1,
                }}
              >
                <HomeIcon sx={{ fontSize: 36 }} />
              </Avatar>
              <Typography variant="h5" fontWeight={700} textAlign="center">
                You've been invited!
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                You've been invited to join a shared household budget on SoldiKeeper.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Household details */}
            <Stack spacing={1.5} mb={3}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Household
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {inviteData.householdName}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Invited by
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {inviteData.owner?.avatar && (
                    <Avatar
                      src={inviteData.owner.avatar}
                      sx={{ width: 24, height: 24 }}
                    />
                  )}
                  <Typography variant="body1" fontWeight={600}>
                    {inviteData.owner?.name || inviteData.owner?.email || 'Household Owner'}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Your role
                </Typography>
                <Chip
                  label={ROLE_LABELS[inviteData.role] || inviteData.role || 'Viewer'}
                  color={ROLE_COLORS[inviteData.role] || 'default'}
                  size="small"
                />
              </Stack>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Actions */}
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/dashboard')}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleAccept}
                disabled={accepting}
                startIcon={accepting ? <CircularProgress size={18} /> : <CheckCircleIcon />}
              >
                {accepting ? 'Accepting…' : 'Accept Invite'}
              </Button>
            </Stack>
          </CardContent>
        )}

        {/* ── Success ──────────────────────────────────────────────────── */}
        {status === 'accepted' && (
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Invite Accepted!
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              You've joined <strong>{inviteData?.householdName}</strong>.
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Redirecting to dashboard…
            </Typography>
          </CardContent>
        )}
      </Card>
    </Box>
  );
}
